import { Button, CircularProgress, Tooltip } from "@mui/material";
import {
  downloadHazardPdf,
  getCrsAnalysisResult,
  getCrsComponentAnalysisResult,
  getFutureHazardAnalysisResult
} from "api/layer";
import { ReactComponent as AddLocationSvg } from "assets/icon_add_location.svg";
import { ReactComponent as LocationPinSvg } from "assets/pin_location.svg";
import Icon from "components/icon/icon";
import Input from "components/input/input";
import Modal from "components/modal/modal";
import sortItem from "components/results/analysis-results/sortUtil";
import Results from "components/results/results";
import domtoimage from "dom-to-image";
import { LeftPanel, RightPanel } from "helpers/constants";
import { AppAction, AppContext, setLeftPanel } from "helpers/context";
import { DrawShapeSource } from "helpers/models";
import { getLanguage } from "helpers/translations";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import generate from "report/generation";
import getDoc from "report/location/document";
import getDocMerge from "report/merge/document";
import { ClimateRiskReportSection, ClimateRiskSspData } from "report/crs/types";
import FATHOM_LAYER_ARRAY from "../../components/fathomAnalyze/id";
import Layers from "./layers/layers";
import RecentEvents from "./recentEvents/recent-events";
import SelectedEvents from "./selectedEvents/selected-events";
import styles from "./single-location.module.scss";

interface Detail {
  [key: string]: string;
}

interface HailAdditionalDetails {
  "HailHazard-31-year-historical-data-set": {
    "Hail Hazard": string;
    "Hail Days (31 year-average)": string;
    "31-year trend": string;
    "Mean Hail Probability (31-year data)": string;
  };
  "HailHazard-64-year-historical-data-set": {
    "Hail Hazard": string;
    "Hail Days (64 year-average)": string;
    "64-year trend": string;
    "Mean Hail Probability (64-year data)": string;
  };
}

type ClimateRiskLookupFn = (batchId: string) => Promise<any>;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getScoreItems = (res: any): any[] => {
  const result = res?.data?.results?.[0];

  return (
    result?.climateRiskScores ||
    result?.futureHazardRiskScores ||
    result?.crsComponentRiskScores ||
    []
  );
};

const buildSspData = (scoreItems: any[]): ClimateRiskSspData => {
  let SSP126Arr: any[] = [],
    SSP245Arr: any[] = [],
    SSP370Arr: any[] = [],
    SSP585Arr: any[] = [];

  scoreItems.forEach((item: any) => {
    item.scenarios.forEach((scenario: any) => {
      let arr: any[] = [],
        isSeaLevelRise = 0;
      arr.push(item.index);
      scenario.bandGroups[0].bands.forEach((band: any) => {
        if (band.value === "NaN") {
          arr.push("");
        } else {
          if (
            item.index === "sea_level_rise" &&
            scenario.name === "SSP3-7.0" &&
            band.value * 1 !== 0
          ) {
            isSeaLevelRise++;
          }
          arr.push((band.value * 1).toFixed(2));
        }
      });
      if (scenario.name === "SSP1-2.6") {
        SSP126Arr.push(arr);
      } else if (scenario.name === "SSP2-4.5") {
        SSP245Arr.push(arr);
      } else if (scenario.name === "SSP3-7.0") {
        if (arr[0] === "sea_level_rise" && isSeaLevelRise === 0) {
          SSP370Arr.push([
            "sea_level_rise",
            "NaN",
            "NaN",
            "NaN",
            "NaN",
            "NaN",
            "NaN",
            "NaN",
            "NaN",
            "NaN"
          ]);
        } else {
          SSP370Arr.push(arr);
        }
      } else if (scenario.name === "SSP5-8.5") {
        SSP585Arr.push(arr);
      }
    });
  });

  if (SSP126Arr.length != SSP370Arr.length) {
    SSP370Arr.unshift([
      "drought_old",
      "NaN",
      "NaN",
      "NaN",
      "NaN",
      "NaN",
      "NaN",
      "NaN",
      "NaN",
      "NaN"
    ]);
  }

  return { SSP126Arr, SSP245Arr, SSP370Arr, SSP585Arr };
};

const fetchClimateRiskSspData = async (
  batchId: string | undefined,
  lookupFn: ClimateRiskLookupFn
): Promise<ClimateRiskSspData | undefined> => {
  if (!batchId || batchId === "running" || batchId === "error") {
    return undefined;
  }

  for (let requestCount = 0; requestCount < 10; requestCount++) {
    try {
      const res: any = await lookupFn(batchId);

      if (res.data.status !== "Completed") {
        if (requestCount === 9) {
          return undefined;
        }
        await sleep(3000);
        continue;
      }

      return buildSspData(getScoreItems(res));
    } catch (error) {
      if (requestCount === 9) {
        return undefined;
      }
      await sleep(3000);
    }
  }

  return undefined;
};

function SingleLocation() {
  const [context, dispatch] = useContext(AppContext);
  const [t] = useTranslation();
  const [downloadModal, setDownloadModal] = useState(false);
  // const [isLoading, setIsLoading] = useState("0"); //0未开始 1下载中 2下载完成
  const [isLoading, setIsLoading] = useState(false); // true下载中 false下载完成
  const [downloadName, setDownloadName] = useState("");

  const openModal = (): void => {
    dispatch({ type: AppAction.setSingleAnalyseModal, payload: { type: "1" } });
  };

  const handleGoPatchLoaction = () => {
    dispatch({
      type: AppAction.setEditMultipleLocation,
      payload: context.currentMultipleLocationInfo
    });

    dispatch({
      type: AppAction.setLeftPanel,
      payload: LeftPanel.EditLocation
    });
  };

  const handleDeleteLoaction = () => {
    dispatch({
      type: AppAction.setDeleteLocationModal,
      payload: context.currentMultipleLocationInfo
    });
  };

  const closeLocation = (): void => {
    goBlack();
  };

  const downloadPdf = async () => {
    if (isLoading) return;
    if (
      !context.analysisData ||
      typeof context.analysisData.legendItems === "string" ||
      typeof context.analysisData.analysisItems === "string" ||
      !context.analysisData.legendItems ||
      !context.analysisData.analysisItems
    ) {
      return;
    }

    if (!context.singleLocationAnalysed) {
      dispatch({
        type: AppAction.addSnackBarParams,
        payload: {
          title: "reportGenerated",
          titleIconColor: "yellow",
          titleIcon: "warning",
          resMessage: t("snackBar.hazardDataRequired")
        }
      });
      return;
    }

    const analysisItems = context.analysisData.analysisItems;
    const legendItems = context.analysisData.legendItems;

    if (!analysisItems.length || !legendItems.length) {
      dispatch({
        type: AppAction.addSnackBarParams,
        payload: {
          title: "reportGenerated",
          titleIconColor: "yellow",
          titleIcon: "warning",
          resMessage: t("snackBar.hazardDataRequired")
        }
      });
      return;
    }

    const selectedLayers = context.singleAnalysisSelectedLayers || [];
    const hasClimateCategory = !!selectedLayers[1]?.length;
    const hasFutureHazardCategory = !!selectedLayers[2]?.length;
    const hasCrsComponentCategory = !!selectedLayers[3]?.length;
    const shouldRenderCrsSections =
      hasClimateCategory || hasFutureHazardCategory || hasCrsComponentCategory;

    const buildSection = (
      category: ClimateRiskReportSection["category"],
      title: string,
      sspData: ClimateRiskSspData | undefined
    ): ClimateRiskReportSection => {
      if (sspData) {
        return {
          category,
          title,
          sspData,
          isEmpty: false
        };
      }

      // Map category to empty description key
      const emptyReasonMap: Record<string, string> = {
        climate: "report.crsSectionEmptyDesc",
        futureHazard: "report.futureClimateRiskEmptyDesc",
        CRSComponent: "report.climateVariablesEmptyDesc"
      };

      // Create empty sspData structure
      const emptySSPData: ClimateRiskSspData = {
        SSP126Arr: [],
        SSP245Arr: [],
        SSP370Arr: [],
        SSP585Arr: []
      };

      return {
        category,
        title,
        sspData: emptySSPData,
        isEmpty: true,
        emptyReason: emptyReasonMap[category]
      };
    };

    const [climateSspData, futureHazardSspData, crsComponentSspData] =
      await Promise.all([
        fetchClimateRiskSspData(context.batchId, getCrsAnalysisResult),
        fetchClimateRiskSspData(
          context.futureHazardBatchId,
          getFutureHazardAnalysisResult
        ),
        fetchClimateRiskSspData(
          context.crsComponentBatchId,
          getCrsComponentAnalysisResult
        )
      ]);

    if (climateSspData) {
      dispatch({
        type: AppAction.setSspData,
        payload: climateSspData
      });
    }

    const sections: ClimateRiskReportSection[] = [];
    if (shouldRenderCrsSections) {
      sections.push(
        buildSection(
          "climate",
          t("options.climate"),
          hasClimateCategory ? climateSspData : undefined
        ),
        buildSection(
          "futureHazard",
          t("options.futureHazard"),
          hasFutureHazardCategory ? futureHazardSspData : undefined
        ),
        buildSection(
          "CRSComponent",
          t("options.CRSComponent"),
          hasCrsComponentCategory ? crsComponentSspData : undefined
        )
      );
    }

    let lng =
      !context.currentMultipleLocationInfo && context.searchLocationInfo
        ? context.searchLocationInfo?.[context.language]?.location?.split(
            ","
          )[0]
        : context.currentMultipleLocationInfo.longitude;
    let lat =
      !context.currentMultipleLocationInfo && context.searchLocationInfo
        ? context.searchLocationInfo?.[context.language]?.location?.split(
            ","
          )[1]
        : context.currentMultipleLocationInfo.latitude;

    sortItem(legendItems);

    sortItem(analysisItems);

    let province;
    if (!context.currentMultipleLocationInfo) {
      province =
        getLanguage() === "en"
          ? context.searchLocationInfo.en.province
          : context.searchLocationInfo.cn.province;
    } else {
      province = context.currentMultipleLocationInfo.province;
    }

    let county;
    if (!context.currentMultipleLocationInfo) {
      county =
        getLanguage() === "en"
          ? context.searchLocationInfo.en.district
          : context.searchLocationInfo.cn.district;
    } else {
      county = context.currentMultipleLocationInfo.place;
    }

    province = province || "";
    county = county || "";

    setIsLoading(true);
    setDownloadModal(false);
    getDocMerge({
      t,
      name: downloadName,
      lng,
      lat,
      province,
      county,
      analysisItems,
      legendItems,
      sections
    })
      .then((dd) => {
        generate(dd, downloadName)
          .then(() => {
            dispatch({
              type: AppAction.addSnackBarParams,
              payload: {
                title: "reportGenerated",
                message: "reportGeneratedSuccess"
              }
            });
          })
          .catch(() => {
            dispatch({
              type: AppAction.addSnackBarParams,
              payload: {
                title: "reportGenerated",
                titleIconColor: "yellow",
                titleIcon: "warning",
                resMessage: t("climateRiskModal.downloadError")
              }
            });
          })
          .finally(() => {
            setIsLoading(false);
          });
      })
      .catch(() => {
        dispatch({
          type: AppAction.addSnackBarParams,
          payload: {
            title: "reportGenerated",
            titleIconColor: "yellow",
            titleIcon: "warning",
            resMessage: t("climateRiskModal.downloadError")
          }
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const downloadHazard = async () => {
    if (isLoading) return;
    if (
      !context.analysisData ||
      typeof context.analysisData.legendItems === "string" ||
      typeof context.analysisData.analysisItems === "string" ||
      !context.analysisData.legendItems ||
      !context.analysisData.analysisItems
    ) {
      return;
    }

    let lng =
      !context.currentMultipleLocationInfo && context.searchLocationInfo
        ? context.searchLocationInfo?.[context.language]?.location?.split(
            ","
          )[0]
        : context.currentMultipleLocationInfo.longitude;
    let lat =
      !context.currentMultipleLocationInfo && context.searchLocationInfo
        ? context.searchLocationInfo?.[context.language]?.location?.split(
            ","
          )[1]
        : context.currentMultipleLocationInfo.latitude;

    const analysisItems = context.analysisData?.analysisItems;
    const legendItems = context.analysisData?.legendItems;

    let province;
    if (!context.currentMultipleLocationInfo) {
      province =
        getLanguage() === "en"
          ? context.searchLocationInfo.en.province
          : context.searchLocationInfo.cn.province;
    } else {
      province = context.currentMultipleLocationInfo.province;
    }

    let county;
    if (!context.currentMultipleLocationInfo) {
      county =
        getLanguage() === "en"
          ? context.searchLocationInfo.en.district
          : context.searchLocationInfo.cn.district;
    } else {
      county = context.currentMultipleLocationInfo.place;
    }

    province = province || "";
    county = county || "";

    setIsLoading(true);
    setDownloadModal(false);
    getDoc({
      t,
      name: downloadName,
      lng,
      lat,
      province,
      county,
      analysisItems,
      legendItems
    })
      .then((dd) => {
        generate(dd, downloadName)
          .then(() => {
            dispatch({
              type: AppAction.addSnackBarParams,
              payload: {
                title: "reportGenerated",
                message: "reportGeneratedSuccess"
              }
            });
          })
          .catch(() => {
            dispatch({
              type: AppAction.addSnackBarParams,
              payload: {
                title: "reportGenerated",
                titleIconColor: "yellow",
                titleIcon: "warning",
                resMessage: t("climateRiskModal.downloadError")
              }
            });
          })
          .finally(() => {
            setIsLoading(false);
          });
      })
      .catch(() => {
        dispatch({
          type: AppAction.addSnackBarParams,
          payload: {
            title: "reportGenerated",
            titleIconColor: "yellow",
            titleIcon: "warning",
            resMessage: t("climateRiskModal.downloadError")
          }
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const goBlack = () => {
    dispatch({
      type: AppAction.setClimateRiskCategorySspData,
      payload: undefined
    });
    dispatch({ type: AppAction.setClimateRiskModal, payload: false });
    dispatch({ type: AppAction.setSearchLocationInfo, payload: undefined });
    dispatch({ type: AppAction.setSingleLocationAnalysed, payload: false });
    dispatch({
      type: AppAction.setCurrentMultipleLocationInfo,
      payload: undefined
    });
    if (context.currentShape) {
      const currentShapeSource = (context.currentShape as any)?.source;
      const targetShapePanel =
        currentShapeSource === DrawShapeSource.POLYGON ||
        currentShapeSource === DrawShapeSource.SHAPE_POLYGON
          ? RightPanel.Polygon
          : RightPanel.Line;
      dispatch({ type: AppAction.setRightPanel, payload: targetShapePanel });
    } else if (
      context.currentMultipleLocations ||
      context.currentMultipleLocationInfo
    ) {
      dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Multiple });
    } else {
      dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Empty });
    }
    dispatch({ type: AppAction.setMapCenter, payload: undefined });
  };

  const captureScreen = async (dom: any) => {
    return await domtoimage.toPng(dom);
  };

  const addLocation = () => {
    setLeftPanel(dispatch, LeftPanel.AddLocation);
  };

  return (
    <div className={styles.single}>
      <div className="overflow-auto flex-1">
        <div className={styles.title}>
          <div className="flex items-center overflow-hidden">
            {(context.currentMultipleLocations || context.currentShape) && (
              <Icon icon="arrow_back" onClick={goBlack} />
            )}
            <div className="truncate">
              {context.currentMultipleLocationInfo
                ? context.currentMultipleLocationInfo.name
                : context.searchLocationInfo[context.language]
                    .formatted_address}
            </div>
          </div>
          <div className="flex items-center">
            {context.currentMultipleLocationInfo && (
              <Tooltip title={t("buttons.edit")} placement="top">
                <span>
                  <Icon icon="create" onClick={handleGoPatchLoaction} />
                </span>
              </Tooltip>
            )}
            {context.currentMultipleLocationInfo && (
              <Tooltip title={t("buttons.delete")} placement="top">
                <span>
                  <Icon icon="delete" onClick={handleDeleteLoaction} />
                </span>
              </Tooltip>
            )}
            <Tooltip title={t("single.addLocation")} placement="top">
              <div className={styles.icon}>
                <AddLocationSvg onClick={addLocation} />
              </div>
            </Tooltip>
            <div>
              <Icon onClick={closeLocation} />
            </div>
          </div>
        </div>
        <div className={styles.info}>
          <div className={styles.image}>
            <LocationPinSvg />
          </div>
          <div className={styles.address}>
            <div>
              <div>
                {context.currentMultipleLocationInfo
                  ? context.currentMultipleLocationInfo.place
                  : context.searchLocationInfo[context.language]
                      .formatted_address}
              </div>
              <div style={{ fontSize: "13px" }}>
                {(() => {
                  if (context.currentMultipleLocationInfo) {
                    const longitude =
                      context.currentMultipleLocationInfo.coordinateSystem ===
                      "GCJ02"
                        ? context.currentMultipleLocationInfo.longitudeGcj
                        : context.currentMultipleLocationInfo.longitudeWgs;
                    const latitude =
                      context.currentMultipleLocationInfo.coordinateSystem ===
                      "GCJ02"
                        ? context.currentMultipleLocationInfo.latitudeGcj
                        : context.currentMultipleLocationInfo.latitudeWgs;
                    return `${longitude},${latitude}`;
                  } else {
                    return context.searchLocationInfo[context.language]
                      ?.location;
                  }
                })()}
              </div>
            </div>

            {context.currentMultipleLocationInfo ? (
              <div>
                {t("single.province")}
                <span className={styles.province}>
                  {context.currentMultipleLocationInfo.province}
                </span>
              </div>
            ) : (
              context.searchLocationInfo[context.language].province && (
                <div>
                  {t("single.province")}
                  <span className={styles.province}>
                    {context.searchLocationInfo[context.language].province}
                  </span>
                </div>
              )
            )}
          </div>
        </div>
        {!context.singleLocationAnalysed && <Layers />}
        {!context.singleLocationAnalysed && <SelectedEvents />}
        {!context.singleLocationAnalysed && <RecentEvents />}
        <Results />
        {/* layers info for a single point is too complex, so disabled for now: {context.singleLocationAnalysed ? <Results /> : <Layers />} */}
      </div>
      <div className={styles.buttons}>
        {context.singleLocationAnalysed &&
          context.analysisData?.legendItems &&
          typeof context.analysisData?.legendItems !== "string" && (
            <div className="w-fit mr-3">
              <Tooltip title={t("buttons.downloadAllTip")} placement="top">
                <Button
                  variant="outlined"
                  color="success"
                  disabled={isLoading}
                  endIcon={
                    isLoading && <CircularProgress color="success" size={24} />
                  }
                  onClick={() => {
                    setDownloadModal(true);
                    setDownloadName("");
                  }}
                >
                  {t(
                    !context.batchId
                      ? "buttons.download"
                      : "buttons.downloadAll"
                  )}
                </Button>
              </Tooltip>
            </div>
          )}
        {context.menu?.find((item) => item.id === "102") && (
          <Button
            color="success"
            variant="contained"
            className="w-full flex-1"
            onClick={openModal}
          >
            {t("buttons.analyseLocations")}
          </Button>
        )}
      </div>

      {downloadModal && (
        <Modal
          header={t("single.downloadTitle")}
          opened={downloadModal}
          onClose={() => setDownloadModal(false)}
        >
          <div className={styles.analyse}>
            <div className="mt-5 mb-5">
              {/* {isLoading === "0" && ( */}
              <Input
                value={downloadName}
                isClose={true}
                placeholder={t("single.downloadPlaceholder")}
                onChange={(event) => setDownloadName(event.target.value)}
                onClose={() => setDownloadName("")}
              />
              {/* )}
              {isLoading === "1" && (
                <div className="text-center">
                  <div className="loading loading-spinner loading-lg"></div>
                  <div>{t("single.downloading")}</div>
                </div>
              )}
              {isLoading === "2" && (
                <div className="text-center">
                  <div>{t("single.downloadCompletes")}</div>
                </div>
              )} */}
            </div>
            <div className="flex justify-between mt-5">
              <div>
                {/* {isLoading === "0" && ( */}
                <Button onClick={() => setDownloadName("")} color="success">
                  {t("buttons.reset")}
                </Button>
                {/* )} */}
              </div>
              <div>
                <Button onClick={() => setDownloadModal(false)} color="success">
                  {t("buttons.close")}
                </Button>
                {/* {isLoading === "0" && ( */}
                <Button
                  variant="outlined"
                  color="success"
                  disabled={isLoading || !downloadName}
                  endIcon={
                    isLoading && <CircularProgress color="success" size={20} />
                  }
                  onClick={downloadPdf}
                >
                  {t("buttons.download")}
                </Button>
                {/* )} */}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default SingleLocation;
